import {
  REVISION_ANALYSIS_DATA_KEYS,
  ANALYSIS_RED,
  ANALYSIS_BLUE,
  REVISION_ESTIMATES_SUBCOLUMNS,
  SURPRISE_ANALYSIS_DATA_KEYS,
  HISTORICAL_SURPRISE_SUBCOLUMNS,
  PAGE_NAME,
} from "src/app/common-constants";
import { analysisColor } from "src/app/utils/common";

interface ComparisonGrid {
  title: string;
  staticColumns?: Array<any>;
  dynamicColumns: Array<any>;
  rowData: Array<any>;
  footerData?: any;
  columnParentMapping: any;
  tickerWithDataCount: number;
}

export function formatRevSupCell(data, currency, unit) {
  const sliCompsData = data;
  if (sliCompsData === undefined || sliCompsData === null) {
    return "-";
  }

  if (sliCompsData === "NM") {
    return "NM";
  }

  const valueToUse = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Math.abs(sliCompsData));

  currency = currency || "";
  let unitType = unit;
  if (unitType && unit !== "%" && unit !== "bps") {
    unitType = "";
  }
  if (sliCompsData < 0) {
    return `(${currency}${valueToUse}${unitType})`;
  }
  return `${currency}${valueToUse}${unitType}`;
}

function getItemsSpecicAndNonSpecificToLines(columnKeys) {
  const lineSpecificItems = [];
  const nonLineSpecificItems = [];
  for (const column of columnKeys) {
    if (column.isLineItemColumn) {
      if (column.subColumns && column.subColumns.length) {
        for (const subCol of column.subColumns) {
          lineSpecificItems.push(subCol.id);
        }
      } else {
        lineSpecificItems.push(column.id);
      }
    } else {
      nonLineSpecificItems.push(column.id);
    }
  }
  return {
    lineSpecificItems,
    nonLineSpecificItems,
  };
}
function createRevSupComparisonGridRowData(
  tickerInfo,
  lineItemIdArray,
  pageName,
) {
  const result = [];
  for (const item of tickerInfo) {
    const obj = {
      ticker: item.ticker,
      cid: item.companyid || item.cid,
      tickerCanBeDeleted: item.tickerCanBeDeleted,
      companyName: item.full_name,
      dataExists: true,
    };
    const _ratioObj = {};
    const columnsDetails =
      pageName === PAGE_NAME.industrySurprise
        ? SURPRISE_ANALYSIS_DATA_KEYS
        : REVISION_ANALYSIS_DATA_KEYS;
    const { lineSpecificItems, nonLineSpecificItems } =
      getItemsSpecicAndNonSpecificToLines(columnsDetails);
    const lienItemSubColumnsId = lineSpecificItems;
    const nonColumnLineItemsId = nonLineSpecificItems;

    for (const lineItemId of lineItemIdArray) {
      for (let i = 0; i < lienItemSubColumnsId.length; i++) {
        const subCol = lienItemSubColumnsId[i];

        _ratioObj[subCol] = {
          formatted: undefined,
          raw: undefined,
          currency: undefined,
          rd: undefined,
        };
      }

      obj[lineItemId] = _ratioObj;
    }

    for (const nonLineItem of nonColumnLineItemsId) {
      obj[nonLineItem] = {
        formatted: undefined,
        raw: undefined,
      };
    }
    result.push(obj);
  }
  return result;
}

function updateRevSupComparisionGridRowData(
  _filledGridData,
  dataResponse,
  lineitemIds,
  pageName,
) {
  const updatedRows = [..._filledGridData.rowData];
  let tickerBindindCounter = 0;
  let tickerWithData = 0;
  for (let i = 0; i < _filledGridData.rowData.length; i++) {
    if (tickerBindindCounter >= Object.keys(dataResponse).length) {
      break;
    }
    const _row = _filledGridData.rowData[i];
    if (dataResponse[_row.ticker]) {
      tickerBindindCounter++;
      const columnsDetails =
        pageName === PAGE_NAME.industrySurprise
          ? SURPRISE_ANALYSIS_DATA_KEYS
          : REVISION_ANALYSIS_DATA_KEYS;
      const { lineSpecificItems, nonLineSpecificItems } =
        getItemsSpecicAndNonSpecificToLines(columnsDetails);
      for (const item of nonLineSpecificItems) {
        if (dataResponse[_row.ticker] && item === "relative_period_Y1") {
          _row[item] = {
            raw: dataResponse[_row.ticker][item],
            formatted: dataResponse[_row.ticker][item] || "-",
          };
        } else if (
          dataResponse[_row.ticker] &&
          dataResponse[_row.ticker][item]
        ) {
          _row[item] = {
            raw: dataResponse[_row.ticker][item]["cal_v"],
            formatted: dataResponse[_row.ticker][item]["v"] || "-",
          };
        } else {
          _row[item] = {
            raw: undefined,
            formatted: "-",
          };
        }
      }

      for (const lineitem of lineitemIds) {
        for (const item of lineSpecificItems) {
          let currentItem = {};
          if (
            dataResponse[_row.ticker] &&
            dataResponse[_row.ticker][lineitem] &&
            dataResponse[_row.ticker][lineitem][item]
          ) {
            const currency = dataResponse[_row.ticker][lineitem][item]["c"];
            const unit = dataResponse[_row.ticker][lineitem][item]["u"];
            const value = dataResponse[_row.ticker][lineitem][item]["v"];
            currentItem = {
              raw: dataResponse[_row.ticker][lineitem][item]["v"],
              formatted: formatRevSupCell(
                dataResponse[_row.ticker][lineitem][item]["v"],
                currency,
                unit,
              ),
              isNegative: typeof value === "number" && value < 0,
              rd: dataResponse[_row.ticker][lineitem][item]["rid"],
              isForecast:
                dataResponse[_row.ticker][lineitem][item]["is_forecast"],
              currency,
              unit,
              bgColor: "",
            };
          } else {
            currentItem = {
              raw: undefined,
              formatted: "-",
            };
          }

          _row[lineitem][item] = currentItem;
          _row;
        }
        _row.dataExists = dataResponse[_row.ticker]
          ? dataResponse[_row.ticker][`${lineitem}|data_exists`]
          : false;
        if (_row.dataExists) {
          tickerWithData++;
        }

        _row.rd = dataResponse[_row.ticker][`${lineitem}|RD`]
          ? dataResponse[_row.ticker][`${lineitem}|RD`]
          : undefined;
        _row.p_m = dataResponse[_row.ticker]["p_m"]
          ? dataResponse[_row.ticker]["p_m"]
          : undefined;
      }
      updatedRows[i] = _row;
    }
  }
  return { updatedRows, tickerWithData };
}

function getColorSensitivityOfCell(columns, lineIds, _gridData) {
  const updatedGridData = { ..._gridData };
  for (let i = 0; i < columns.length; i++) {
    const subItem = columns[i];
    let highestValue = 0;
    let lowestValue = 0;
    for (const lineId of lineIds) {
      for (const item of updatedGridData.rowData) {
        const value = item[lineId][subItem].raw;
        if (typeof value === "number") {
          if (value >= highestValue) {
            highestValue = value;
          } else if (value < lowestValue) {
            lowestValue = value;
          }
        }
      }
      for (let j = 0; j < updatedGridData.rowData.length; j++) {
        const value = updatedGridData.rowData[j][lineId][subItem].raw;
        if (typeof value === "number") {
          if (value > 0) {
            updatedGridData.rowData[j][lineId][subItem].bgColor = analysisColor(
              value,
              highestValue,
              ANALYSIS_BLUE,
            );
          } else if (value < 0) {
            updatedGridData.rowData[j][lineId][subItem].bgColor = analysisColor(
              value,
              lowestValue,
              ANALYSIS_RED,
            );
          }
        }
      }
    }
  }
  return { ...updatedGridData };
}

export function createRevSupColumnParentMapping(lineId, pageName) {
  let result = {};
  const columnsDetails =
    pageName === PAGE_NAME.industrySurprise
      ? SURPRISE_ANALYSIS_DATA_KEYS
      : REVISION_ANALYSIS_DATA_KEYS;
  for (const item of columnsDetails) {
    if (item.isLineItemColumn) {
      if (item.subColumns && item.subColumns.length) {
        for (const subItem of item.subColumns) {
          result = {
            ...result,
            [subItem.id]: lineId,
          };
        }
      } else {
        result = {
          ...result,
          [item.id]: lineId,
        };
      }
    }
  }
  return result;
}

export function createRevSupComparisonGridData(tickerInfo, lineItem, pageName) {
  const _gridData: ComparisonGrid = {
    title: "",
    dynamicColumns: [],
    rowData: [],
    footerData: undefined,
    columnParentMapping: undefined,
    tickerWithDataCount: 0,
  };
  _gridData.title = lineItem.formatted_name || lineItem.name || "";
  const columnsDetails =
    pageName === PAGE_NAME.industrySurprise
      ? SURPRISE_ANALYSIS_DATA_KEYS
      : REVISION_ANALYSIS_DATA_KEYS;
  _gridData.dynamicColumns = [...columnsDetails];
  _gridData.columnParentMapping = createRevSupColumnParentMapping(
    lineItem.uniquesliid,
    pageName,
  );
  _gridData.rowData = createRevSupComparisonGridRowData(
    tickerInfo,
    [lineItem.uniquesliid],
    pageName,
  );

  return _gridData;
}

function getColumnIdForRevisionSensitivity() {
  return REVISION_ESTIMATES_SUBCOLUMNS.map((item) => item.id);
}

function getColumnIdForSurpriseSensitivity() {
  return HISTORICAL_SURPRISE_SUBCOLUMNS.filter(
    (item) => item.id !== "quarter_average_surprise",
  ).map((item) => item.id);
}

export function updateRevSupComparisonGridData(
  gridData,
  dataResponse,
  lineItem,
  pageName,
) {
  let updatedGridData = { ...gridData };
  if (dataResponse) {
    const lineitemIdArray = [lineItem.uniquesliid];
    const { updatedRows, tickerWithData } = updateRevSupComparisionGridRowData(
      updatedGridData,
      dataResponse,
      lineitemIdArray,
      pageName,
    );
    updatedGridData.rowData = [...updatedRows];
    updatedGridData.tickerWithDataCount =
      updatedGridData.tickerWithDataCount + tickerWithData;
    const sensitivityColumns =
      pageName === PAGE_NAME.industrySurprise
        ? getColumnIdForSurpriseSensitivity()
        : getColumnIdForRevisionSensitivity();

    updatedGridData = getColorSensitivityOfCell(
      sensitivityColumns,
      lineitemIdArray,
      updatedGridData,
    );
  }
  return updatedGridData;
}
